import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const RAZORPAY_KEY = 'rzp_live_SyswhY6CmR3gMx';
const TRACKING_FEE = 199;

const T = {
  bg:          '#0f172a',
  bgCard:      '#1e293b',
  bgCardAlt:   '#162032',
  border:      '#334155',
  orange:      '#f59e0b',
  orangeHover: '#fbbf24',
  blue:        '#3b82f6',
  green:       '#10b981',
  red:         '#ef4444',
  white:       '#f8fafc',
  muted:       '#94a3b8',
  dim:         '#64748b',
};

const pulseStyle = `
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes spin  { to{transform:rotate(360deg)} }
  @keyframes glow  { 0%,100%{box-shadow:0 0 8px rgba(16,185,129,0.6)} 50%{box-shadow:0 0 20px rgba(16,185,129,0.9)} }
  html, body, #root { background-color: #0f172a !important; margin: 0; }
  .nav-link { color: #94a3b8; text-decoration: none; font-size: 13px; font-weight: 600; padding: 6px 12px; border-radius: 8px; transition: all 0.2s; }
  .nav-link:hover { color: #f8fafc; background: rgba(255,255,255,0.08); }
  .nav-btn-orange { background: #f59e0b; color: #0f172a; text-decoration: none; font-size: 13px; font-weight: 800; padding: 7px 16px; border-radius: 8px; transition: all 0.2s; white-space: nowrap; }
  .nav-btn-orange:hover { background: #fbbf24; transform: translateY(-1px); box-shadow: 0 4px 15px rgba(245,158,11,0.4); }
  .nav-btn-outline { color: #f8fafc; text-decoration: none; font-size: 13px; font-weight: 700; padding: 7px 16px; border-radius: 8px; border: 1px solid #334155; transition: all 0.2s; white-space: nowrap; }
  .nav-btn-outline:hover { border-color: #f59e0b; color: #f59e0b; }
  .hamburger-menu { display: none; }
  .mobile-dropdown { display: none; flex-direction: column; gap: 4px; padding: 12px; background: #1e293b; border-top: 1px solid #334155; }
  .mobile-dropdown.open { display: flex; }
  .mobile-nav-link { color: #94a3b8; text-decoration: none; font-size: 14px; font-weight: 600; padding: 10px 14px; border-radius: 8px; transition: background 0.2s; }
  .mobile-nav-link:hover { background: rgba(255,255,255,0.06); color: #f8fafc; }
  .mobile-nav-btn { background: #f59e0b; color: #0f172a; text-decoration: none; font-size: 14px; font-weight: 800; padding: 10px 14px; border-radius: 8px; text-align: center; margin-top: 4px; }
  .mobile-nav-signin { color: #f8fafc; text-decoration: none; font-size: 14px; font-weight: 700; padding: 10px 14px; border-radius: 8px; border: 1px solid #334155; text-align: center; }
  @media (max-width: 768px) {
    .desktop-nav { display: none !important; }
    .hamburger-menu { display: flex !important; }
  }
  .sh-tracking-map { z-index: 1; }
  .sh-popup .leaflet-popup-content-wrapper { background: #1e293b !important; border: 1px solid #334155 !important; border-radius: 10px !important; box-shadow: 0 8px 25px rgba(0,0,0,0.6) !important; padding: 0 !important; }
  .sh-popup .leaflet-popup-content { margin: 0 !important; }
  .sh-popup .leaflet-popup-tip { background: #1e293b !important; }
  .sh-popup .leaflet-popup-close-button { color: #94a3b8 !important; font-size: 18px !important; top: 6px !important; right: 8px !important; }
  .leaflet-control-attribution { display: none !important; }
  .leaflet-control-zoom { border: 1px solid #334155 !important; }
  .leaflet-control-zoom a { background: #1e293b !important; color: #f8fafc !important; border-bottom: 1px solid #334155 !important; }
`;

// ── Module-level helpers ──────────────────────────────────────
const formatTime = (str) => {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return str; }
};

const formatDateShort = (str) => {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch { return '—'; }
};

const sortCrossings = (arr) =>
  [...arr].sort((a, b) => new Date(a.reader_read_time || 0) - new Date(b.reader_read_time || 0));

const parseGeocode = (geocode) => {
  if (!geocode) return null;
  const parts = geocode.split(',').map(Number);
  if (parts.length !== 2 || parts.some(isNaN)) return null;
  return parts;
};

const getGoogleMapsUrl = (sorted) => {
  const coords = sorted.map(tc => parseGeocode(tc.toll_plaza_geocode)).filter(Boolean);
  if (!coords.length) return null;
  if (coords.length === 1) return `https://www.google.com/maps?q=${coords[0].join(',')}`;
  return `https://www.google.com/maps/dir/${coords.map(c => c.join(',')).join('/')}`;
};

// ── Disclaimer text (shared) ──────────────────────────────────
const DISCLAIMER_TEXT =
  'Toll crossing data is sourced from government FASTag systems (NPCI / NHAI). ' +
  'SugarHathi represents this data as-is from government records and is not responsible for ' +
  'delays, gaps, or missing crossings. ₹199 is non-refundable once paid.';

const OTP_DISCLAIMER_TEXT =
  'Tracking data is sourced directly from government FASTag systems (NPCI / NHAI). ' +
  'SugarHathi is a technology platform that represents government records as-is and is not ' +
  'responsible for any delays, gaps, or missing toll crossings.';

// ── Component ─────────────────────────────────────────────────
const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const vehicleNumber = searchParams.get('v')?.trim().toUpperCase().replace(/\s/g, '') || '';

  const [pageState,        setPageState]        = useState('loading');
  const [crossings,        setCrossings]         = useState([]);
  const [mobileInput,      setMobileInput]       = useState('');
  const [otpInput,         setOtpInput]          = useState('');
  const [otpSent,          setOtpSent]           = useState(false);
  const [otpLoading,       setOtpLoading]        = useState(false);
  const [otpError,         setOtpError]          = useState('');
  const [otpDisclaimerOk,  setOtpDisclaimerOk]  = useState(false); // OTP page disclaimer
  const [payLoading,       setPayLoading]        = useState(false);
  const [bookingId,        setBookingId]         = useState('');
  const [readableId,       setReadableId]        = useState('');
  const [isFree,           setIsFree]            = useState(false);
  const [message,          setMessage]           = useState('');
  const [hoveredBtn,       setHoveredBtn]        = useState('');
  const [menuOpen,         setMenuOpen]          = useState(false);
  const [fastagActive,     setFastagActive]      = useState(null); // null=unknown, true=active, false=inactive/no-record
  const [disclaimerChecked,setDisclaimerChecked] = useState(false); // payment page disclaimer

  const mapContainerRef = useRef(null);
  const leafletMapRef   = useRef(null);

  // ── localStorage helpers ────────────────────────────────────
  const getStoredMobile = () => localStorage.getItem(`sh_track_mobile_${vehicleNumber}`) || '';
  const storeMobile     = (m) => localStorage.setItem(`sh_track_mobile_${vehicleNumber}`, m);
  const clearMobile     = () => localStorage.removeItem(`sh_track_mobile_${vehicleNumber}`);

  // ── Load Razorpay + Leaflet ─────────────────────────────────
  useEffect(() => {
    const rzpScript = document.createElement('script');
    rzpScript.src = 'https://checkout.razorpay.com/v1/checkout.js';
    rzpScript.async = true;
    document.body.appendChild(rzpScript);

    const lfCSS = document.createElement('link');
    lfCSS.rel = 'stylesheet';
    lfCSS.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(lfCSS);

    const lfScript = document.createElement('script');
    lfScript.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    lfScript.async = true;
    document.body.appendChild(lfScript);

    document.body.style.backgroundColor = '#0f172a';
    document.documentElement.style.backgroundColor = '#0f172a';

    return () => {
      if (document.body.contains(rzpScript)) document.body.removeChild(rzpScript);
      if (document.body.contains(lfScript))  document.body.removeChild(lfScript);
      if (document.head.contains(lfCSS))     document.head.removeChild(lfCSS);
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  // ── Leaflet map — init/update when crossings change ─────────
  useEffect(() => {
    if (!crossings.length) return;

    const initMap = () => {
      if (!mapContainerRef.current || !window.L) return;

      const sorted = sortCrossings(crossings);
      const coords = sorted.map(tc => parseGeocode(tc.toll_plaza_geocode)).filter(Boolean);
      if (!coords.length) return;

      const L = window.L;

      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }

      const map = L.map(mapContainerRef.current, {
        zoomControl: true,
        attributionControl: false,
        scrollWheelZoom: false,
      });

      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 18,
        subdomains: 'abcd',
      }).addTo(map);

      if (coords.length > 1) {
        L.polyline(coords, {
          color: '#f59e0b',
          weight: 3,
          opacity: 0.9,
          dashArray: '8, 5',
        }).addTo(map);
      }

      sorted.forEach((tc, i) => {
        const coord = parseGeocode(tc.toll_plaza_geocode);
        if (!coord) return;
        const isLatest = i === sorted.length - 1;
        const size = isLatest ? 36 : 26;

        const icon = L.divIcon({
          className: '',
          html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            background:${isLatest ? '#10b981' : '#f59e0b'};
            border:3px solid ${isLatest ? '#34d399' : '#fbbf24'};
            display:flex;align-items:center;justify-content:center;
            font-size:${isLatest ? '15px' : '10px'};font-weight:800;
            color:${isLatest ? '#fff' : '#0f172a'};
            box-shadow:0 0 ${isLatest ? '16px rgba(16,185,129,0.75)' : '6px rgba(245,158,11,0.5)'};
            ${isLatest ? 'animation:glow 2s infinite;' : ''}
          ">${isLatest ? '🚛' : i + 1}</div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });

        L.marker(coord, { icon })
          .addTo(map)
          .bindPopup(
            L.popup({ className: 'sh-popup', maxWidth: 220 }).setContent(`
              <div style="padding:12px 16px;">
                <div style="color:#f59e0b;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">
                  ${isLatest ? '📍 Last seen' : `Stop ${i + 1}`}
                </div>
                <div style="color:#f8fafc;font-size:14px;font-weight:800;margin-bottom:6px;">
                  ${tc.toll_plaza_name || 'Toll Plaza'}
                </div>
                <div style="color:#94a3b8;font-size:12px;">
                  ${formatTime(tc.reader_read_time)}
                </div>
                ${isLatest ? '<div style="color:#10b981;font-size:11px;font-weight:700;margin-top:6px;">🟢 Live position</div>' : ''}
              </div>
            `)
          );
      });

      if (coords.length === 1) {
        map.setView(coords[0], 13);
      } else {
        map.fitBounds(L.latLngBounds(coords), { padding: [40, 40] });
      }

      leafletMapRef.current = map;
    };

    if (window.L) {
      initMap();
    } else {
      const timer = setInterval(() => {
        if (window.L) { clearInterval(timer); initMap(); }
      }, 150);
      return () => clearInterval(timer);
    }
  }, [crossings]);

  // ── Fetch tracking data ─────────────────────────────────────
  const fetchTrackingData = useCallback(async (mobile = '') => {
    if (!vehicleNumber) { setPageState('invalid'); return; }
    try {
      // Poll FASTag first — non-critical
      try {
        await supabase.functions.invoke('poll-toll-crossings', {
          body: { vehicle_number: vehicleNumber },
        });
      } catch (pollErr) {
        console.warn('FASTag poll (non-critical):', pollErr);
      }

      const { data, error } = await supabase.functions.invoke('get-tracking-data', {
        body: { vehicle_number: vehicleNumber, mobile_phone: mobile },
      });
      if (error) throw error;
      switch (data.status) {
        case 'invalid':
          setPageState('invalid');
          setMessage(data.message);
          break;
        case 'expired':
          setPageState('expired');
          break;
        case 'otp_required':
          clearMobile();
          setPageState('otp_required');
          break;
        case 'payment_required':
          setPageState('payment_required');
          setBookingId(data.booking_id);
          setReadableId(data.readable_booking_id);
          // fastag_active: true = Active, false = Inactive or no record
          setFastagActive(data.fastag_active === true);
          break;
        case 'verified':
          setPageState('verified');
          setCrossings(data.crossings || []);
          setIsFree(data.is_free);
          setReadableId(data.readable_booking_id);
          break;
        default:
          setPageState('error');
      }
    } catch (err) {
      console.error('fetchTrackingData error:', err);
      setPageState('error');
    }
  }, [vehicleNumber]);

  // ── On page load ────────────────────────────────────────────
  useEffect(() => {
    if (!vehicleNumber) { setPageState('invalid'); return; }
    const storedMobile = getStoredMobile();
    if (storedMobile) {
      fetchTrackingData(storedMobile);
    } else {
      setPageState('otp_required');
    }
  }, [vehicleNumber]);

  // ── Auto-refresh every 45 min ───────────────────────────────
  useEffect(() => {
    if (pageState !== 'verified') return;
    const interval = setInterval(() => fetchTrackingData(getStoredMobile()), 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [pageState, fetchTrackingData]);

  // ── Send OTP ────────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (!mobileInput || mobileInput.length < 10) {
      setOtpError('Enter a valid 10-digit mobile number');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      const mobile = mobileInput.startsWith('+91') ? mobileInput : `+91${mobileInput}`;
      const { data, error } = await supabase.functions.invoke('verify-tracking-otp', {
        body: { action: 'send_otp', vehicle_number: vehicleNumber, mobile_phone: mobile },
      });
      if (error) throw error;
      if (data?.success) {
        setOtpSent(true);
      } else {
        setOtpError(data?.message || 'Failed to send OTP. Try again.');
      }
    } catch {
      setOtpError('Failed to send OTP. Try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Verify OTP ──────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (!otpInput || otpInput.length < 4) {
      setOtpError('Enter the OTP sent to your WhatsApp');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      const mobile = mobileInput.startsWith('+91') ? mobileInput : `+91${mobileInput}`;
      const { data, error } = await supabase.functions.invoke('verify-tracking-otp', {
        body: { action: 'verify_otp', vehicle_number: vehicleNumber, mobile_phone: mobile, otp_code: otpInput },
      });
      if (error) throw error;
      if (data?.success) {
        storeMobile(mobile);
        await fetchTrackingData(mobile);
      } else {
        setOtpError(data?.message || 'Invalid OTP. Try again.');
      }
    } catch {
      setOtpError('Verification failed. Try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Razorpay payment ────────────────────────────────────────
  const handlePayment = async () => {
    setPayLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-razorpay-order', {
        body: {
          booking_id: bookingId,
          vehicle_number: vehicleNumber,
          paid_by_mobile: getStoredMobile(),
        },
      });
      if (error) throw error;
      const storedMobile = getStoredMobile();
      const options = {
        key: RAZORPAY_KEY,
        amount: data.amount,
        currency: data.currency,
        name: 'SugarHathi',
        description: `Vehicle Tracking — ${vehicleNumber}`,
        order_id: data.order_id,
        handler: async (response) => {
          const { data: vd, error: ve } = await supabase.functions.invoke('verify-razorpay-payment', {
            body: {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: bookingId,
              vehicle_number: vehicleNumber,
              paid_by_mobile: storedMobile,
            },
          });
          if (ve || !vd?.success) {
            alert('Payment verification failed. Contact support.');
            return;
          }
          await fetchTrackingData(storedMobile);
        },
        prefill: { contact: storedMobile || '' },
        theme: { color: '#f59e0b' },
        modal: { ondismiss: () => setPayLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch {
      alert('Could not initiate payment. Try again.');
    } finally {
      setPayLoading(false);
    }
  };

  // ── Style helpers ───────────────────────────────────────────
  const btnOrange = (id, disabled = false) => ({
    backgroundColor: hoveredBtn === id && !disabled ? T.orangeHover : T.orange,
    color: '#0f172a', border: 'none', borderRadius: 12,
    padding: '14px 24px', fontWeight: 800, fontSize: 15,
    cursor: disabled ? 'not-allowed' : 'pointer',
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'all 0.2s', opacity: disabled ? 0.5 : 1,
    transform: hoveredBtn === id && !disabled ? 'translateY(-1px)' : 'none',
    boxShadow: hoveredBtn === id && !disabled ? '0 8px 25px rgba(245,158,11,0.45)' : '0 4px 15px rgba(245,158,11,0.2)',
  });

  const btnGreen = (id, disabled = false) => ({
    backgroundColor: hoveredBtn === id && !disabled ? '#059669' : T.green,
    color: '#fff', border: 'none', borderRadius: 12,
    padding: '14px 24px', fontWeight: 800, fontSize: 15,
    cursor: disabled ? 'not-allowed' : 'pointer',
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'all 0.2s', opacity: disabled ? 0.5 : 1,
    transform: hoveredBtn === id && !disabled ? 'translateY(-1px)' : 'none',
    boxShadow: hoveredBtn === id && !disabled ? '0 8px 25px rgba(16,185,129,0.4)' : '0 4px 15px rgba(16,185,129,0.15)',
  });

  const card = { backgroundColor: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 };
  const inputStyle = { backgroundColor: T.bgCardAlt, border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 16px', color: T.white, fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const pageWrap = { minHeight: '100vh', backgroundColor: T.bg, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" };
  const inner = { maxWidth: 600, margin: '0 auto', padding: '32px 16px' };

  // ── Shared disclaimer box ───────────────────────────────────
  const DisclaimerBox = ({ text, checked, onChange }) => (
    <div style={{
      backgroundColor: 'rgba(245,158,11,0.06)',
      border: `1px solid rgba(245,158,11,0.25)`,
      borderRadius: 10, padding: '14px 16px', marginBottom: 16,
    }}>
      <p style={{ color: T.muted, fontSize: 12, lineHeight: 1.7, margin: '0 0 12px' }}>
        ⚠️ {text}
      </p>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          style={{ marginTop: 2, width: 16, height: 16, accentColor: T.orange, flexShrink: 0 }}
        />
        <span style={{ color: T.white, fontSize: 13, fontWeight: 600 }}>
          I understand and agree to proceed
        </span>
      </label>
    </div>
  );

  // ── Navbar ──────────────────────────────────────────────────
  const Navbar = () => (
    <nav style={{ backgroundColor: '#0d1526', borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 50 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}` }}>
            <img src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png" alt="SugarHathi" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div>
            <div style={{ color: T.white, fontWeight: 800, fontSize: 15, lineHeight: 1.1 }}>SugarHathi</div>
            <div style={{ color: T.dim, fontSize: 10 }}>India's Organised Sugar Economy</div>
          </div>
        </a>
        <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <a href="/sugar-marketplace-dashboard" className="nav-link">Browse Sugar Offers</a>
          <a href="/truck-marketplace" className="nav-link">Browse Verified Trucks</a>
          <a href="/sell-your-sugar" className="nav-link">Sell Your Sugar</a>
          <a href="/sugar-marketplace-dashboard" className="nav-btn-orange" style={{ marginLeft: 8 }}>Browse Sugar →</a>
          <a href="/login-authentication" className="nav-btn-outline" style={{ marginLeft: 6 }}>Sign In</a>
        </div>
        <button className="hamburger-menu" onClick={() => setMenuOpen(!menuOpen)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 5, padding: 8 }}>
          <span style={{ width: 22, height: 2, backgroundColor: menuOpen ? T.orange : T.white, borderRadius: 2, transition: 'all 0.2s', transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
          <span style={{ width: 22, height: 2, backgroundColor: T.white, borderRadius: 2, opacity: menuOpen ? 0 : 1, transition: 'all 0.2s' }} />
          <span style={{ width: 22, height: 2, backgroundColor: menuOpen ? T.orange : T.white, borderRadius: 2, transition: 'all 0.2s', transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
        </button>
      </div>
      <div className={`mobile-dropdown ${menuOpen ? 'open' : ''}`}>
        <a href="/sugar-marketplace-dashboard" className="mobile-nav-link">🛒 Browse Sugar Offers</a>
        <a href="/truck-marketplace" className="mobile-nav-link">🚛 Browse Verified Trucks</a>
        <a href="/sell-your-sugar" className="mobile-nav-link">📦 Sell Your Sugar</a>
        <a href="/sugar-marketplace-dashboard" className="mobile-nav-btn">Browse Sugar Offers →</a>
        <a href="/login-authentication" className="mobile-nav-signin">Sign In →</a>
      </div>
    </nav>
  );

  // ── Vehicle Badge ───────────────────────────────────────────
  const VehicleBadge = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
      <div style={{ backgroundColor: T.bgCardAlt, border: `2px solid ${T.orange}`, borderRadius: 14, padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 26 }}>🚛</span>
        <div>
          <p style={{ color: T.dim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: 0 }}>Vehicle Number</p>
          <p style={{ color: T.orange, fontWeight: 900, fontSize: 22, letterSpacing: 3, margin: 0 }}>{vehicleNumber}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: T.green, display: 'inline-block', animation: 'pulse 2s infinite' }} />
          <span style={{ color: T.green, fontSize: 11, fontWeight: 700 }}>Live</span>
        </div>
      </div>
    </div>
  );

  // ── Loading ─────────────────────────────────────────────────
  if (pageState === 'loading') return (
    <div style={pageWrap}>
      <style>{pulseStyle}</style>
      <Navbar />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 44, height: 44, border: `3px solid ${T.orange}`, borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: T.muted, fontSize: 14 }}>Loading tracking data...</p>
        </div>
      </div>
    </div>
  );

  // ── Invalid ─────────────────────────────────────────────────
  if (pageState === 'invalid') return (
    <div style={pageWrap}>
      <style>{pulseStyle}</style>
      <Helmet><title>Tracking — SugarHathi</title></Helmet>
      <Navbar />
      <div style={{ ...inner, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <div style={{ ...card, textAlign: 'center', maxWidth: 420, width: '100%' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🚫</div>
          <h2 style={{ color: T.white, fontWeight: 800, fontSize: 20, marginBottom: 8 }}>Tracking Not Available</h2>
          <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            {message || 'This tracking link is not valid. Please check the link sent to you and try again.'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <a href="/sugar-marketplace-dashboard" style={{ ...btnOrange('inv1'), textDecoration: 'none' }}
              onMouseEnter={() => setHoveredBtn('inv1')} onMouseLeave={() => setHoveredBtn('')}>
              🛒 Browse Sugar Offers
            </a>
            <a href="/login-authentication" style={{ display: 'block', textAlign: 'center', color: T.muted, fontSize: 13, padding: 10, textDecoration: 'none' }}>
              Sign In to your account →
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Expired ─────────────────────────────────────────────────
  if (pageState === 'expired') return (
    <div style={pageWrap}>
      <style>{pulseStyle}</style>
      <Helmet><title>Tracking Expired — SugarHathi</title></Helmet>
      <Navbar />
      <div style={{ ...inner, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <div style={{ ...card, textAlign: 'center', maxWidth: 420, width: '100%' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>⏰</div>
          <h2 style={{ color: T.white, fontWeight: 800, fontSize: 20, marginBottom: 8 }}>Tracking Link Expired</h2>
          <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            This tracking link was valid for 4 days and has now expired. Contact your supplier for a new tracking link.
          </p>
          <a href="/sugar-marketplace-dashboard" style={{ ...btnOrange('exp1'), textDecoration: 'none' }}
            onMouseEnter={() => setHoveredBtn('exp1')} onMouseLeave={() => setHoveredBtn('')}>
            🛒 Browse Sugar Offers
          </a>
        </div>
      </div>
    </div>
  );

  // ── OTP Required ────────────────────────────────────────────
  if (pageState === 'otp_required') return (
    <div style={pageWrap}>
      <style>{pulseStyle}</style>
      <Helmet><title>Verify — SugarHathi</title></Helmet>
      <Navbar />
      <div style={inner}>
        <VehicleBadge />
        <div style={card}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>💬</div>
            <h2 style={{ color: T.white, fontWeight: 800, fontSize: 20, marginBottom: 6 }}>Verify Your WhatsApp</h2>
            <p style={{ color: T.muted, fontSize: 13, lineHeight: 1.7 }}>
              Enter your mobile number. We'll send a one-time OTP on WhatsApp to verify your identity before showing live tracking.
            </p>
          </div>

          {/* Disclaimer shown before OTP entry */}
          <DisclaimerBox
            text={OTP_DISCLAIMER_TEXT}
            checked={otpDisclaimerOk}
            onChange={setOtpDisclaimerOk}
          />

          {!otpSent ? (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ backgroundColor: T.bgCardAlt, border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px', color: T.white, fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                  🇮🇳 +91
                </div>
                <input type="tel" value={mobileInput}
                  onChange={e => setMobileInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit mobile number" style={inputStyle} />
              </div>
              {otpError && <p style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>{otpError}</p>}
              <button style={btnOrange('sendotp', otpLoading || mobileInput.length < 10 || !otpDisclaimerOk)}
                onMouseEnter={() => setHoveredBtn('sendotp')} onMouseLeave={() => setHoveredBtn('')}
                onClick={handleSendOtp} disabled={otpLoading || mobileInput.length < 10 || !otpDisclaimerOk}>
                {otpLoading
                  ? <div style={{ width: 20, height: 20, border: '2px solid #0f172a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  : '📲 Send OTP on WhatsApp'}
              </button>
            </>
          ) : (
            <>
              <div style={{ backgroundColor: 'rgba(16,185,129,0.1)', border: `1px solid ${T.green}`, borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: T.green, fontSize: 18 }}>✓</span>
                <span style={{ color: T.muted, fontSize: 13 }}>OTP sent to <strong style={{ color: T.white }}>+91{mobileInput}</strong> on WhatsApp</span>
              </div>
              <input type="number" value={otpInput}
                onChange={e => setOtpInput(e.target.value.slice(0, 6))}
                placeholder="Enter OTP"
                style={{ ...inputStyle, textAlign: 'center', fontSize: 28, fontWeight: 800, letterSpacing: '0.5em', marginBottom: 12 }} />
              {otpError && <p style={{ color: '#f87171', fontSize: 12, marginBottom: 12, textAlign: 'center' }}>{otpError}</p>}
              <button style={{ ...btnGreen('verifyotp', otpLoading || otpInput.length < 4), marginBottom: 10 }}
                onMouseEnter={() => setHoveredBtn('verifyotp')} onMouseLeave={() => setHoveredBtn('')}
                onClick={handleVerifyOtp} disabled={otpLoading || otpInput.length < 4}>
                {otpLoading
                  ? <div style={{ width: 20, height: 20, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  : '✓ Verify & View Live Tracking'}
              </button>
              <button onClick={() => { setOtpSent(false); setOtpInput(''); setOtpError(''); }}
                style={{ background: 'none', border: 'none', color: T.dim, fontSize: 12, cursor: 'pointer', width: '100%', textAlign: 'center', padding: 8 }}>
                Wrong number? Change
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ── Payment Required (Sugar Only) ───────────────────────────
  if (pageState === 'payment_required') return (
    <div style={pageWrap}>
      <style>{pulseStyle}</style>
      <Helmet><title>Activate Tracking — SugarHathi</title></Helmet>
      <Navbar />
      <div style={inner}>
        <VehicleBadge />
        <div style={{ ...card, marginBottom: 16 }}>
          <h2 style={{ color: T.white, fontWeight: 800, fontSize: 20, marginBottom: 6, textAlign: 'center' }}>Activate Vehicle Tracking</h2>

          {/* FASTag status badge */}
          {fastagActive ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{
                backgroundColor: 'rgba(16,185,129,0.12)',
                color: T.green,
                border: `1px solid rgba(16,185,129,0.35)`,
                borderRadius: 20, padding: '5px 16px',
                fontSize: 13, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: T.green, display: 'inline-block', animation: 'pulse 2s infinite' }} />
                FASTag Active
              </span>
            </div>
          ) : (
            // Inactive or no record — show message, hide payment entirely
            <div style={{
              backgroundColor: 'rgba(239,68,68,0.08)',
              border: `1px solid rgba(239,68,68,0.3)`,
              borderRadius: 12, padding: '20px 20px',
              textAlign: 'center', marginBottom: 8,
            }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📡</div>
              <p style={{ color: '#fca5a5', fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
                {fastagActive === false
                  ? 'FASTag Not Active'
                  : 'FASTag Status Not on Record'}
              </p>
              <p style={{ color: T.muted, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                {fastagActive === false
                  ? 'Live toll tracking is unavailable for this vehicle.'
                  : 'Live toll tracking is unavailable for this vehicle.'}
              </p>
            </div>
          )}

          {/* Only show payment flow if FASTag is active */}
          {fastagActive === true && (
            <>
              <p style={{ color: T.muted, fontSize: 13, textAlign: 'center', marginBottom: 24, lineHeight: 1.7 }}>
                One-time payment. Track this truck live via government FASTag data. Share with your entire supply chain — free.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
                {[['🗺️', 'Live map with route'], ['📍', 'Real-time toll crossings'], ['🔗', 'Share with anyone'], ['📅', 'Valid 4 days']].map(([icon, text]) => (
                  <div key={text} style={{ backgroundColor: T.bgCardAlt, border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>{icon}</span>
                    <span style={{ color: T.muted, fontSize: 12, fontWeight: 600 }}>{text}</span>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <span style={{ color: T.dim, fontSize: 13 }}>One-time · </span>
                <span style={{ color: T.orange, fontWeight: 900, fontSize: 32 }}>₹{TRACKING_FEE}</span>
              </div>

              {/* Payment disclaimer + checkbox */}
              <DisclaimerBox
                text={DISCLAIMER_TEXT}
                checked={disclaimerChecked}
                onChange={setDisclaimerChecked}
              />

              <button style={btnOrange('pay', payLoading || !disclaimerChecked)}
                onMouseEnter={() => setHoveredBtn('pay')} onMouseLeave={() => setHoveredBtn('')}
                onClick={handlePayment} disabled={payLoading || !disclaimerChecked}>
                {payLoading
                  ? <div style={{ width: 20, height: 20, border: '2px solid #0f172a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  : `⚡ Pay ₹${TRACKING_FEE} & Activate Tracking`}
              </button>
              <p style={{ color: T.dim, fontSize: 11, textAlign: 'center', marginTop: 12 }}>🔒 Secured by Razorpay · UPI / Card / Net Banking</p>
            </>
          )}
        </div>

        <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, padding: 16 }}>
          {[['50+', 'Verified Mills'], ['600+', 'Transporters'], ['₹500Cr+', 'Moved Safely']].map(([val, label]) => (
            <a key={label} href="/sugar-marketplace-dashboard" style={{ textAlign: 'center', textDecoration: 'none' }}>
              <p style={{ color: T.orange, fontWeight: 900, fontSize: 18, margin: 0 }}>{val}</p>
              <p style={{ color: T.dim, fontSize: 11, margin: 0 }}>{label}</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Verified — Full Tracking View ───────────────────────────
  const sorted = sortCrossings(crossings);
  const lastCrossing = sorted[sorted.length - 1];
  const googleMapsUrl = getGoogleMapsUrl(sorted);

  return (
    <div style={pageWrap}>
      <style>{pulseStyle}</style>
      <Helmet><title>Tracking {vehicleNumber} — SugarHathi</title></Helmet>
      <Navbar />

      <div style={inner}>

        {/* ── Vehicle card ── */}
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <p style={{ color: T.dim, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 4px' }}>Vehicle</p>
              <p style={{ color: T.orange, fontWeight: 900, fontSize: 24, letterSpacing: 2, margin: 0 }}>🚛 {vehicleNumber}</p>
              {readableId && <p style={{ color: T.dim, fontSize: 11, fontFamily: 'monospace', marginTop: 4 }}>{readableId}</p>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              {isFree ? (
                <span style={{ backgroundColor: 'rgba(16,185,129,0.15)', color: T.green, border: `1px solid ${T.green}`, borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                  ✓ Platform Tracking
                </span>
              ) : (
                <span style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: T.orange, border: `1px solid ${T.orange}`, borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                  ✓ Activated
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: T.green, display: 'inline-block', animation: 'pulse 2s infinite' }} />
                <span style={{ color: T.green, fontSize: 11, fontWeight: 600 }}>Live · Auto-refreshes every 45 min</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Live Journey Map ── */}
        {sorted.length > 0 && (
          <div style={{ ...card, marginBottom: 16, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <h2 style={{ color: T.white, fontWeight: 800, fontSize: 15, margin: '0 0 3px' }}>🗺️ Live Journey Map</h2>
                <p style={{ color: T.muted, fontSize: 12, margin: 0 }}>
                  Last seen at <strong style={{ color: T.green }}>{lastCrossing?.toll_plaza_name || '—'}</strong>
                  {lastCrossing?.reader_read_time && (
                    <span style={{ color: T.dim }}> · {formatTime(lastCrossing.reader_read_time)}</span>
                  )}
                </p>
              </div>
              {googleMapsUrl && (
                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer"
                  style={{
                    backgroundColor: 'rgba(59,130,246,0.12)',
                    color: T.blue,
                    border: `1px solid rgba(59,130,246,0.3)`,
                    borderRadius: 8, padding: '7px 13px',
                    fontSize: 12, fontWeight: 700,
                    textDecoration: 'none',
                    display: 'flex', alignItems: 'center', gap: 5,
                    whiteSpace: 'nowrap',
                  }}>
                  🗺 Full Route ↗
                </a>
              )}
            </div>
            <div ref={mapContainerRef} className="sh-tracking-map"
              style={{ height: 300, width: '100%', background: '#0d1a2e' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderTop: `1px solid ${T.border}` }}>
              {[
                ['📍', 'Plazas', String(sorted.length)],
                ['🕐', 'First', formatDateShort(sorted[0]?.reader_read_time)],
                ['🟢', 'Latest', formatDateShort(lastCrossing?.reader_read_time)],
              ].map(([icon, label, val], idx) => (
                <div key={label} style={{
                  padding: '10px 12px', textAlign: 'center',
                  borderRight: idx < 2 ? `1px solid ${T.border}` : 'none',
                }}>
                  <p style={{ color: T.dim, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7, margin: '0 0 2px' }}>{icon} {label}</p>
                  <p style={{ color: T.white, fontWeight: 800, fontSize: 14, margin: 0 }}>{val}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Toll Timeline ── */}
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ color: T.white, fontWeight: 800, fontSize: 16, margin: 0 }}>📍 Toll Timeline</h2>
            <span style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: T.orange, border: `1px solid rgba(245,158,11,0.25)`, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>
              {sorted.length} {sorted.length === 1 ? 'crossing' : 'crossings'}
            </span>
          </div>

          {sorted.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 52, marginBottom: 12 }}>🛣️</div>
              <p style={{ color: T.white, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Journey in progress</p>
              <p style={{ color: T.muted, fontSize: 13, lineHeight: 1.7 }}>
                Toll crossings appear here as the vehicle passes FASTag toll plazas. Page auto-refreshes every 45 minutes.
              </p>
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: T.green, display: 'inline-block', animation: 'pulse 2s infinite' }} />
                <span style={{ color: T.green, fontSize: 12, fontWeight: 700 }}>Live monitoring active</span>
              </div>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 11, top: 0, bottom: 0, width: 2, backgroundColor: T.border }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {sorted.map((tc, i) => {
                  const isLatest = i === sorted.length - 1;
                  return (
                    <div key={tc.id || tc.seq_no || i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        backgroundColor: isLatest ? T.green : T.bgCard,
                        border: `2px solid ${isLatest ? T.green : T.orange}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, zIndex: 1, marginTop: 2,
                      }}>
                        {isLatest
                          ? <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fff', display: 'block', animation: 'pulse 2s infinite' }} />
                          : <span style={{ color: T.orange, fontSize: 9, fontWeight: 800 }}>{i + 1}</span>
                        }
                      </div>
                      <div style={{
                        flex: 1,
                        backgroundColor: isLatest ? 'rgba(16,185,129,0.08)' : T.bgCardAlt,
                        border: `1px solid ${isLatest ? 'rgba(16,185,129,0.3)' : T.border}`,
                        borderRadius: 12, padding: '12px 16px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                          <p style={{ color: T.white, fontWeight: 700, fontSize: 14, margin: 0 }}>
                            {tc.toll_plaza_name || 'Toll Plaza'}
                          </p>
                          {isLatest && (
                            <span style={{ backgroundColor: T.green, color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                              LATEST
                            </span>
                          )}
                        </div>
                        <p style={{ color: isLatest ? T.green : T.orange, fontSize: 12, fontWeight: 600, margin: '4px 0 0' }}>
                          🕐 {formatTime(tc.reader_read_time)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Platform CTA ── */}
        <div style={{ ...card, textAlign: 'center', padding: 20 }}>
          <p style={{ color: T.white, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>🐘 SugarHathi — India's Organised Sugar Economy</p>
          <p style={{ color: T.dim, fontSize: 12, marginBottom: 16 }}>50+ Mills · 600+ Transporters · ₹500Cr+ Moved Safely</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <a href="/sugar-marketplace-dashboard" className="nav-btn-orange">🛒 Browse Sugar Offers</a>
            <a href="/truck-marketplace" className="nav-btn-orange">🚛 Browse Verified Trucks</a>
            <a href="/login-authentication" className="nav-btn-outline">Sign In →</a>
          </div>
        </div>

        <p style={{ color: T.dim, fontSize: 11, textAlign: 'center', marginTop: 16, paddingBottom: 16 }}>
          Powered by <strong style={{ color: T.orange }}>SugarHathi</strong> · FASTag Data (Govt. of India) · <span style={{ color: T.green }}>🔒 Secure</span>
        </p>

      </div>
    </div>
  );
};

export default TrackingPage;
